// src/components/shop/ProductCard.js
import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useCurrency } from '../../context/CurrencyContext';
import { useTheme } from '../../components/ThemeProvider';
import Button from '../common/Button';
import SectionLoader from '../common/SectionLoader';

function ProductCard({ product, formatPrice, isLoading = false }) {
  const { addToCart } = useCart();
  const { primaryColor } = useTheme();

  const handleAddToCart = () => {
    if ((product.category === 'clothing' || product.category === 'shoes') && (!product.sizes?.length || !product.colors?.length)) {
      console.error(`Product ${product.name} missing sizes or colors`);
      return;
    }
    if (product.stock <= 0 && product.has_stock) {
      alert('This product is out of stock.');
      return;
    }
    const cartItem = {
      ...product,
      quantity: 1,
      ...(product.category === 'clothing' || product.category === 'shoes' ? {
        selectedSize: product.sizes[0],
        selectedColor: product.colors[0]
      } : {})
    };
    addToCart(cartItem);
  };

  const renderAction = () => {
    if (product.category === 'real_estate' || product.category === 'cars') {
      return (
        <div className="flex gap-3 mt-5">
          <a
            href={`mailto:${product.contactInfo?.email}`}
            className="flex-1 bg-primary text-cream px-5 py-3 text-base font-montserrat rounded-full hover:bg-primary/90 transition-all duration-300 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40"
            style={{ '--primary': primaryColor }}
          >
            Email
          </a>
          <a
            href={`tel:${product.contactInfo?.phone}`}
            className="flex-1 bg-primary text-cream px-5 py-3 text-base font-montserrat rounded-full hover:bg-primary/90 transition-all duration-300 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40"
            style={{ '--primary': primaryColor }}
          >
            Call
          </a>
        </div>
      );
    }
    return (
      <Button
        onClick={handleAddToCart}
        className="mt-5 w-full bg-primary text-cream px-6 py-4 text-lg font-montserrat rounded-full hover:bg-primary/90 transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary/40"
        disabled={product.stock === 0 && product.has_stock === 1}
        style={{ '--primary': primaryColor }}
      >
        Add to Cart
      </Button>
    );
  };

  if (isLoading) {
    return (
      <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100/60 h-full">
        <SectionLoader message="Loading product..." />
      </div>
    );
  }

  return (
    <div className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:shadow-primary/20 transition-all duration-400 border border-gray-100/60">
      <Link to={`/product/${product.id}`} className="block relative overflow-hidden">
        <img
          src={
            product.imageViews ? process.env.REACT_APP_PRODUCT_URL  +"/"+ Object.values(product.imageViews)[0] :
            process.env.REACT_APP_PRODUCT_URL +"/"+ product.image || '/assets/images/placeholder.jpg'
          }
          alt={product.name}
          className="w-full h-72 sm:h-80 lg:h-96 object-cover transition-transform duration-700 group-hover:scale-105"
          onError={() => console.error(`Failed to load image for ${product.name}`)}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
      </Link>
      <div className="p-5 sm:p-6 space-y-4">
        <Link to={`/product/${product.id}`}>
          <h3 className="text-xl sm:text-2xl font-playfair-display font-semibold text-primary group-hover:text-primary/80 transition-colors line-clamp-2 leading-tight">
            {product.name}
          </h3>
        </Link>
        <p className="text-lg font-montserrat text-primary font-medium">
          {product.has_stock ? (
            product.stock > 0 ? (
              <span className="text-green-600">In Stock</span>
            ) : (
              <span className="text-red-600">Out of Stock</span>
            )
          ) : null}
        </p>
        <p className="text-xl sm:text-2xl font-montserrat font-bold text-primary tracking-tight">
          {formatPrice ? formatPrice(product.price) : `$${product.price.toFixed(2)}`}
        </p>
        {renderAction()}
      </div>
    </div>
  );
}

export default ProductCard;